// Channel-owned BYOK: encrypt provider keys with a passphrase the
// channel owner shares out-of-band. PBKDF2 (SHA-256, 200k iterations)
// derives an AES-256-GCM key from the passphrase + a per-attachment
// random salt. The encrypted blob lives in the session manifest;
// decryption produces the plaintext keys, kept only in sessionStorage
// (per-tab, not persisted across browser shutdowns).
//
// Web Crypto only — no JS crypto library, no bundle bloat. AES-GCM
// authenticates the ciphertext, so a wrong passphrase fails loudly
// instead of producing garbage that gets accepted downstream.

import type { Provider } from "./session";

export interface PlainKeys {
  babyai?: string;
  anthropic?: string;
}

export interface EncryptedKeys {
  /** Base64 PBKDF2 salt (16 bytes). */
  salt: string;
  /** Base64 AES-GCM IV (12 bytes). */
  iv: string;
  /** Base64 AES-GCM ciphertext. */
  ciphertext: string;
  /** Providers this blob carries — visible without decrypting, lets
   *  the UI tell a joiner what the passphrase unlocks. */
  providers: Provider[];
}

const PBKDF2_ITERATIONS = 200_000;
const PBKDF2_HASH = "SHA-256";
const SALT_BYTES = 16;
const IV_BYTES = 12;

export async function encryptKeys(
  keys: PlainKeys,
  passphrase: string,
): Promise<EncryptedKeys> {
  if (!passphrase) throw new Error("passphrase required");

  const providers: Provider[] = [];
  if (keys.babyai) providers.push("babyai");
  if (keys.anthropic) providers.push("anthropic");
  if (providers.length === 0) throw new Error("no keys to encrypt");

  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const aesKey = await deriveKey(passphrase, salt);

  const json = JSON.stringify(keys);
  const plaintext = new TextEncoder().encode(json);

  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    plaintext,
  );

  return {
    salt: bytesToB64(salt),
    iv: bytesToB64(iv),
    ciphertext: bytesToB64(new Uint8Array(cipher)),
    providers,
  };
}

export async function decryptKeys(
  blob: EncryptedKeys,
  passphrase: string,
): Promise<PlainKeys> {
  if (!passphrase) throw new Error("passphrase required");
  const salt = b64ToBytes(blob.salt);
  const iv = b64ToBytes(blob.iv);
  const ciphertext = b64ToBytes(blob.ciphertext);
  const aesKey = await deriveKey(passphrase, salt);

  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      aesKey,
      ciphertext,
    );
  } catch {
    // AES-GCM throws OperationError on a wrong key — auth tag failure.
    // Surface a user-readable message; the underlying error is
    // intentionally opaque (don't leak which check failed).
    throw new Error("wrong passphrase");
  }

  const json = new TextDecoder().decode(plain);
  const parsed = JSON.parse(json) as PlainKeys;
  return parsed;
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
